import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  X,
  ImagePlus,
  Loader2,
  Save,
  Trash2,
  Grid3x3,
  ArrowUp,
  ArrowDown,
  ArrowLeft,
  ArrowRight,
} from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "react-toastify";
import {
  updateCertificateTemplate,
  uploadCertificateTemplateBackground,
} from "../../../../api/elearning/certificates/certificates";
import {
  CERT_CANVAS,
  CERT_DEFAULTS,
  CERT_FONT_IMPORT_CSS,
  CERT_FONT_OPTIONS,
  CERT_FIELD_LABELS,
  DEFAULT_FIELD_KEYS,
  IEC_PRESET_KEYS,
  getCertificateFontFamily,
  mergeLayoutWithDefaults,
  getCertFieldDisplayValue,
  buildCertificateDisplayData,
  getFieldDefinition,
  isFieldKeyEnabled,
} from "../certificateLayoutUtils";

/** Đồng bộ với multer `fileSize` trên API templates/background */
const TEMPLATE_BG_MAX_MB = 8;

const SAMPLE_DATA = buildCertificateDisplayData({
  recipient_type: "student",
  student_name: "Nguyễn Thị Huyền",
  course_name:
    "Phương pháp tổ chức hoạt động trải nghiệm và giáo dục Kỹ năng sống",
  grade: "Hoàn thành khóa học KNS cấp tiểu học",
  birth_year: "1990",
  organization_unit: "Trường Tiểu học Hà Huy Tập",
  issue_place: "Nghệ An",
  issue_date: "2026-01-08",
  cert_id: "IMA-2026-015",
  register_no: "SVS-2026-00026",
});

function anchorTransform(align) {
  if (align === "left") return "translate(0, -50%)";
  if (align === "right") return "translate(-100%, -50%)";
  return "translate(-50%, -50%)";
}

function textAlignFor(align) {
  if (align === "left") return "left";
  if (align === "right") return "right";
  return "center";
}

function miniAnchor(align) {
  if (align === "left") return "translate(0, -50%)";
  if (align === "right") return "translate(-100%, -50%)";
  return "translate(-50%, -50%)";
}

const POSITION_MIN = 0.02;
const POSITION_MAX = 0.98;

const TEXT_TRANSFORM_OPTIONS = [
  { value: "none", label: "Giữ nguyên" },
  { value: "uppercase", label: "UPPERCASE" },
  { value: "lowercase", label: "lowercase" },
  { value: "capitalize", label: "Capitalize" },
];

const SNAP_STEP_OPTIONS = [
  { value: 0.0025, label: "0.25%" },
  { value: 0.005, label: "0.5%" },
  { value: 0.01, label: "1%" },
  { value: 0.02, label: "2%" },
];

function clampPosition(value) {
  return Math.max(POSITION_MIN, Math.min(POSITION_MAX, Number(value) || 0));
}

function snapPosition(value, enabled, step) {
  const safe = clampPosition(value);
  if (!enabled || !Number.isFinite(Number(step)) || Number(step) <= 0) {
    return safe;
  }
  return clampPosition(Math.round(safe / Number(step)) * Number(step));
}

const TemplateDesignerModal = ({ isOpen, template, onClose, onSaved }) => {
  const [layout, setLayout] = useState(() => mergeLayoutWithDefaults(null));
  const [selectedKey, setSelectedKey] = useState(null);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [localBackgroundUrl, setLocalBackgroundUrl] = useState(null);
  const [snapEnabled, setSnapEnabled] = useState(true);
  const [snapStep, setSnapStep] = useState(0.005);
  const [rightPanelTab, setRightPanelTab] = useState("fields");
  const canvasRef = useRef(null);
  const draggingRef = useRef(null);
  const resizingRef = useRef(null);

  useEffect(() => {
    if (isOpen && template) {
      setLayout(mergeLayoutWithDefaults(template.layout_config));
      setSelectedKey(null);
      setLocalBackgroundUrl(null);
      setRightPanelTab("fields");
    }
  }, [isOpen, template]);

  useEffect(() => {
    if (selectedKey) {
      setRightPanelTab("properties");
    }
  }, [selectedKey]);

  const selectedField = layout.fields?.find((f) => f.key === selectedKey) || null;
  const currentNudgeStep = useMemo(() => (snapEnabled ? snapStep : 0.0025), [snapEnabled, snapStep]);

  const updateFieldPosition = useCallback((key, nextX, nextY) => {
    const x = clampPosition(nextX);
    const y = clampPosition(nextY);
    setLayout((prev) => ({
      ...prev,
      fields: (prev.fields || []).map((f) => (f.key === key ? { ...f, x, y } : f)),
    }));
  }, []);

  const moveField = useCallback((key, clientX, clientY) => {
    const el = canvasRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    let x = (clientX - rect.left) / rect.width;
    let y = (clientY - rect.top) / rect.height;
    x = snapPosition(x, snapEnabled, snapStep);
    y = snapPosition(y, snapEnabled, snapStep);
    updateFieldPosition(key, x, y);
  }, [snapEnabled, snapStep, updateFieldPosition]);

  const onPointerMove = useCallback(
    (e) => {
      if (resizingRef.current) {
        const { key, startX, startWidth } = resizingRef.current;
        const nextWidth = Math.max(60, Math.min(760, startWidth + (e.clientX - startX)));
        setLayout((prev) => ({
          ...prev,
          fields: (prev.fields || []).map((f) =>
            f.key === key ? { ...f, width: nextWidth } : f
          ),
        }));
        return;
      }
      const key = draggingRef.current;
      if (!key) return;
      moveField(key, e.clientX, e.clientY);
    },
    [moveField]
  );

  const endDrag = useCallback(() => {
    draggingRef.current = null;
    resizingRef.current = null;
    window.removeEventListener("pointermove", onPointerMove);
    window.removeEventListener("pointerup", endDrag);
    window.removeEventListener("pointercancel", endDrag);
  }, [onPointerMove]);

  const startDrag = (key, e) => {
    e.preventDefault();
    e.stopPropagation();
    draggingRef.current = key;
    setSelectedKey(key);
    window.addEventListener("pointermove", onPointerMove);
    window.addEventListener("pointerup", endDrag);
    window.addEventListener("pointercancel", endDrag);
  };

  const startResize = (key, currentWidth, e) => {
    e.preventDefault();
    e.stopPropagation();
    resizingRef.current = {
      key,
      startX: e.clientX,
      startWidth: Number(currentWidth) > 0 ? Number(currentWidth) : 200,
    };
    setSelectedKey(key);
    window.addEventListener("pointermove", onPointerMove);
    window.addEventListener("pointerup", endDrag);
    window.addEventListener("pointercancel", endDrag);
  };

  const readAsDataUrl = (file) =>
    new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(String(reader.result || ""));
      reader.onerror = () => reject(new Error("Không thể đọc file ảnh."));
      reader.readAsDataURL(file);
    });

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    setUploading(true);
    try {
      const localDataUrl = await readAsDataUrl(file);
      setLocalBackgroundUrl(localDataUrl);

      try {
        const { url } = await uploadCertificateTemplateBackground(file);
        if (url) {
          setLayout((prev) => ({
            ...prev,
            backgroundUrl: url,
          }));
          setLocalBackgroundUrl((prev) => prev || localDataUrl);
          toast.success("Đã tải ảnh nền.");
          return;
        }
      } catch (uploadErr) {
        toast.warning(
          uploadErr?.response?.data?.message ||
            "Ảnh đang ở chế độ tạm thời (local preview). Lưu thiết kế sẽ không gồm ảnh này."
        );
      }
    } catch (err) {
      toast.error(err.message || err.response?.data?.message || "Không tải được ảnh.");
    } finally {
      setUploading(false);
    }
  };

  const clearBackground = () => {
    setLayout((prev) => ({
      ...prev,
      backgroundUrl: null,
    }));
    setLocalBackgroundUrl(null);
  };

  const updateSelected = (patch) => {
    if (!selectedKey) return;
    setLayout((prev) => ({
      ...prev,
      fields: (prev.fields || []).map((f) =>
        f.key === selectedKey ? { ...f, ...patch } : f
      ),
    }));
  };

  const updateSelectedAxis = (axis, rawValue) => {
    if (!selectedField) return;
    const parsed = parseFloat(rawValue);
    if (!Number.isFinite(parsed)) return;
    const next = snapPosition(parsed / 100, snapEnabled, snapStep);
    updateFieldPosition(
      selectedField.key,
      axis === "x" ? next : selectedField.x ?? 0.5,
      axis === "y" ? next : selectedField.y ?? 0.5
    );
  };

  const nudgeSelected = (dx, dy) => {
    if (!selectedField) return;
    const nextX = snapPosition((selectedField.x ?? 0.5) + dx, snapEnabled, snapStep);
    const nextY = snapPosition((selectedField.y ?? 0.5) + dy, snapEnabled, snapStep);
    updateFieldPosition(selectedField.key, nextX, nextY);
  };

  const setFieldEnabled = (key, enabled) => {
    setLayout((prev) => {
      const current = prev.fields || [];
      if (enabled) {
        if (current.some((f) => f.key === key)) return prev;
        return { ...prev, fields: [...current, getFieldDefinition(key)] };
      }
      return { ...prev, fields: current.filter((f) => f.key !== key) };
    });
    if (!enabled && selectedKey === key) setSelectedKey(null);
  };

  const applyPresetKeys = (keys) => {
    const existing = new Map((layout.fields || []).map((f) => [f.key, f]));
    setLayout((prev) => ({
      ...prev,
      fields: keys.map((key) => ({ ...getFieldDefinition(key), ...existing.get(key) })),
    }));
    setSelectedKey(null);
  };

  const enabledCount = (layout.fields || []).length;

  const handleSave = async () => {
    if (!template?.id) return;
    const backgroundUrl = String(layout.backgroundUrl || "").trim();
    if (!backgroundUrl && localBackgroundUrl) {
      toast.error("Ảnh nền mới chỉ ở local preview. Vui lòng tải ảnh nền lại để upload thành công trước khi lưu.");
      return;
    }
    if (!Array.isArray(layout.fields) || layout.fields.length === 0) {
      toast.error("Chọn ít nhất một trường hiển thị trên giấy.");
      return;
    }
    setSaving(true);
    try {
      const cleanLayout = {
        ...layout,
      };
      delete cleanLayout.backgroundFallbackDataUrl;

      await updateCertificateTemplate(template.id, {
        layout_config: {
          ...cleanLayout,
          version: 2,
          width: CERT_CANVAS.width,
          height: CERT_CANVAS.height,
          enabledKeys: cleanLayout.fields.map((f) => f.key),
          defaults: {
            ...CERT_DEFAULTS,
            ...(cleanLayout.defaults || {}),
          },
        },
      });
      toast.success("Đã lưu thiết kế biểu mẫu.");
      onSaved?.();
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.message || "Không lưu được.");
    } finally {
      setSaving(false);
    }
  };

  if (!isOpen || !template) return null;
  const effectiveBackgroundUrl = localBackgroundUrl || layout.backgroundUrl || null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 z-[120] flex items-center justify-center bg-slate-900/50 backdrop-blur-sm p-3 md:p-6"
        onClick={onClose}
      >
        <motion.div
          initial={{ opacity: 0, scale: 0.98 }}
          animate={{ opacity: 1, scale: 1 }}
          exit={{ opacity: 0, scale: 0.98 }}
          onClick={(e) => e.stopPropagation()}
          className="bg-white rounded-2xl shadow-2xl w-full max-w-6xl max-h-[95vh] flex flex-col overflow-hidden"
        >
          <style>{CERT_FONT_IMPORT_CSS}</style>
          <div className="flex items-center justify-between gap-3 px-5 py-4 border-b border-gray-100 shrink-0">
            <div>
              <h2 className="text-lg font-black text-gray-800 uppercase tracking-tight">
                Template <span className="text-red-700">Designer</span>
              </h2>
              <p className="text-xs text-gray-500 mt-0.5">
                {template.name} — Kéo các nhãn trên khung để đặt vị trí (MVP)
              </p>
            </div>
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={handleSave}
                disabled={saving}
                className="inline-flex items-center gap-2 px-4 py-2 rounded-xl bg-red-700 text-white text-sm font-bold hover:bg-red-800 disabled:opacity-60"
              >
                {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
                Lưu thiết kế
              </button>
              <button
                type="button"
                onClick={onClose}
                className="p-2 rounded-xl hover:bg-gray-100 text-gray-500"
                aria-label="Đóng"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
          </div>

          <div className="flex flex-col lg:flex-row min-h-0 flex-1 overflow-hidden">
            <div className="flex-1 overflow-y-auto p-4 md:p-6 border-b lg:border-b-0 lg:border-r border-gray-100">
              <div className="flex flex-wrap items-center gap-2 mb-4">
                <label className="inline-flex items-center gap-2 px-3 py-2 rounded-xl bg-gray-50 border border-gray-200 text-sm font-bold text-gray-700 cursor-pointer hover:bg-gray-100">
                  {uploading ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <ImagePlus className="h-4 w-4 text-red-600" />
                  )}
                  Tải ảnh nền
                  <input
                    type="file"
                    accept="image/jpeg,image/png,image/gif,image/webp"
                    className="hidden"
                    onChange={handleUpload}
                    disabled={uploading}
                  />
                </label>
                {effectiveBackgroundUrl ? (
                  <button
                    type="button"
                    onClick={clearBackground}
                    className="inline-flex items-center gap-1 px-3 py-2 rounded-xl border border-gray-200 text-sm font-bold text-gray-600 hover:bg-gray-50"
                  >
                    <Trash2 className="h-4 w-4" /> Gỡ nền
                  </button>
                ) : null}
                <label className="inline-flex items-center gap-2 px-3 py-2 rounded-xl bg-white border border-gray-200 text-sm font-bold text-gray-700">
                  <Grid3x3 className="h-4 w-4 text-red-600" />
                  <input
                    type="checkbox"
                    checked={snapEnabled}
                    onChange={(e) => setSnapEnabled(e.target.checked)}
                    className="accent-red-700"
                  />
                  Snap lưới
                </label>
                <select
                  value={String(snapStep)}
                  onChange={(e) => setSnapStep(Number(e.target.value) || 0.005)}
                  className="px-3 py-2 rounded-xl border border-gray-200 bg-white text-sm font-bold text-gray-700"
                >
                  {SNAP_STEP_OPTIONS.map((opt) => (
                    <option key={opt.label} value={opt.value}>
                      Bước {opt.label}
                    </option>
                  ))}
                </select>
                {selectedField ? (
                  <div className="px-3 py-2 rounded-xl bg-red-50 border border-red-100 text-xs font-semibold text-red-700">
                    X: {(((selectedField.x ?? 0.5) * 100).toFixed(2))}% | Y: {(((selectedField.y ?? 0.5) * 100).toFixed(2))}%
                  </div>
                ) : null}
              </div>
              <div className="mb-4 rounded-xl border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-900 leading-relaxed">
                <p className="m-0">
                  Ảnh nền khuyến nghị: <span className="font-bold">842 × 595 px (A4 ngang)</span>. Để in sắc nét hơn
                  có thể dùng <span className="font-bold">1684 × 1190</span> hoặc{" "}
                  <span className="font-bold">2480 × 1754</span> (giữ đúng tỷ lệ A4 ngang).
                </p>
                <p className="m-0 mt-1.5">
                  Dung lượng tối đa: <span className="font-bold">{TEMPLATE_BG_MAX_MB} MB</span>/file. Định dạng:{" "}
                  <span className="font-bold">JPG, PNG, GIF, WEBP</span>. File quá lớn sẽ không upload được — nên nén
                  ảnh (thường dưới 2 MB là đủ cho nền in).
                </p>
              </div>

              <div className="overflow-x-auto flex justify-center pb-2">
                <div
                  ref={canvasRef}
                  className="relative bg-white border-2 border-dashed border-gray-300 rounded-lg shadow-inner shrink-0"
                  style={{
                    width: CERT_CANVAS.width,
                    height: CERT_CANVAS.height,
                    maxWidth: "100%",
                  }}
                  onPointerDown={() => setSelectedKey(null)}
                >
                  {effectiveBackgroundUrl ? (
                    <img
                      src={effectiveBackgroundUrl}
                      alt=""
                      className="absolute inset-0 w-full h-full object-cover pointer-events-none select-none rounded-md"
                      draggable={false}
                    />
                  ) : (
                    <div className="absolute inset-0 bg-gradient-to-br from-gray-50 to-gray-100 rounded-md" />
                  )}
                  <div
                    className="absolute inset-0 pointer-events-none rounded-md"
                    style={{
                      opacity: snapEnabled ? 0.22 : 0.08,
                      backgroundImage: snapEnabled
                        ? `linear-gradient(to right, rgba(239,68,68,0.16) 1px, transparent 1px), linear-gradient(to bottom, rgba(239,68,68,0.16) 1px, transparent 1px)`
                        : `linear-gradient(to right, rgba(148,163,184,0.1) 1px, transparent 1px), linear-gradient(to bottom, rgba(148,163,184,0.1) 1px, transparent 1px)`,
                      backgroundSize: `${Math.max(8, Math.round(CERT_CANVAS.width * snapStep))}px ${Math.max(8, Math.round(CERT_CANVAS.height * snapStep))}px`,
                    }}
                  />
                  <div className="absolute left-1/2 top-0 bottom-0 w-px bg-slate-300/40 pointer-events-none" />
                  <div className="absolute top-1/2 left-0 right-0 h-px bg-slate-300/40 pointer-events-none" />
                  {selectedField ? (
                    <>
                      <div
                        className="absolute top-0 bottom-0 w-px bg-red-500/30 pointer-events-none"
                        style={{ left: `${(selectedField.x ?? 0.5) * 100}%` }}
                      />
                      <div
                        className="absolute left-0 right-0 h-px bg-red-500/30 pointer-events-none"
                        style={{ top: `${(selectedField.y ?? 0.5) * 100}%` }}
                      />
                    </>
                  ) : null}

                  {(layout.fields || []).map((f) => {
                    const label = CERT_FIELD_LABELS[f.key] || f.key;
                    const preview = getCertFieldDisplayValue(f.key, SAMPLE_DATA, layout);
                    const align = f.align || "center";
                    const active = selectedKey === f.key;

                    return (
                      <button
                        key={f.key}
                        type="button"
                        onPointerDown={(e) => startDrag(f.key, e)}
                        className={`absolute z-10 rounded-md px-1.5 py-0.5 text-left transition-shadow ${
                          active
                            ? "ring-2 ring-red-600 shadow-md bg-white/90"
                            : "ring-1 ring-red-400/60 bg-white/80 hover:bg-white/95"
                        }`}
                        style={{
                          left: `${(f.x ?? 0.5) * 100}%`,
                          top: `${(f.y ?? 0.5) * 100}%`,
                          transform: anchorTransform(align),
                          color: f.color || "#111827",
                          fontSize: f.fontSize ?? 18,
                          fontWeight: f.fontWeight || "normal",
                          fontFamily: getCertificateFontFamily(f.fontFamily),
                          fontStyle: f.fontStyle || "normal",
                          textTransform: f.textTransform || "none",
                          textAlign: textAlignFor(align),
                          lineHeight: 1.2,
                          cursor: "grab",
                          width: f.width ? `${f.width}px` : undefined,
                          maxWidth: "85%",
                        }}
                      >
                        <span className="block text-[10px] font-black uppercase tracking-wide text-red-700/80">
                          {label}
                        </span>
                        <span className="block">{preview}</span>
                        {active ? (
                          <span
                            className="absolute -right-2 top-1/2 h-4 w-4 -translate-y-1/2 rounded-full border border-red-500 bg-white shadow-sm cursor-ew-resize"
                            onPointerDown={(e) => startResize(f.key, f.width, e)}
                            title="Kéo để đổi độ rộng"
                          />
                        ) : null}
                      </button>
                    );
                  })}
                </div>
              </div>
              <p className="text-[11px] text-gray-500 mt-3">
                Bật/tắt trường bên phải → chỉ các trường đang bật hiện trên khung. Chọn trường (viền đỏ) để chỉnh cỡ
                chữ, font, độ nghiêng, kiểu chuyển chữ, màu, căn lề; kéo thả để đặt vị trí. Khi cần căn thật đều,
                hãy bật snap lưới và dùng cụm nút dịch chuyển nhỏ ở panel bên phải.
              </p>

              <div className="mt-5 rounded-2xl border border-gray-200 bg-gray-50/80 p-4">
                <div className="flex items-center justify-between gap-3 mb-3">
                  <div>
                    <h3 className="text-xs font-black uppercase text-gray-500 tracking-widest">
                      Mặc định biểu mẫu
                    </h3>
                    <p className="text-[11px] text-gray-500 mt-1">
                      Nhóm này đặt giá trị mặc định chung cho giấy, nên để ngay dưới khung thiết kế để chỉnh và đối chiếu nhanh.
                    </p>
                  </div>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="text-[11px] font-bold text-gray-500 uppercase">Nơi cấp</label>
                    <input
                      type="text"
                      value={layout.defaults?.issue_place ?? CERT_DEFAULTS.issue_place}
                      onChange={(e) =>
                        setLayout((prev) => ({
                          ...prev,
                          defaults: { ...(prev.defaults || CERT_DEFAULTS), issue_place: e.target.value },
                        }))
                      }
                      className="w-full mt-1 bg-white border border-gray-200 rounded-xl py-2 px-3 text-sm"
                    />
                  </div>
                  <div>
                    <label className="text-[11px] font-bold text-gray-500 uppercase">Đơn vị mặc định</label>
                    <input
                      type="text"
                      value={layout.defaults?.organization_unit ?? CERT_DEFAULTS.organization_unit}
                      onChange={(e) =>
                        setLayout((prev) => ({
                          ...prev,
                          defaults: {
                            ...(prev.defaults || CERT_DEFAULTS),
                            organization_unit: e.target.value,
                          },
                        }))
                      }
                      className="w-full mt-1 bg-white border border-gray-200 rounded-xl py-2 px-3 text-sm"
                    />
                  </div>
                  <div>
                    <label className="text-[11px] font-bold text-gray-500 uppercase">Hậu tố số hiệu</label>
                    <input
                      type="text"
                      value={layout.defaults?.cert_id_suffix ?? CERT_DEFAULTS.cert_id_suffix}
                      onChange={(e) =>
                        setLayout((prev) => ({
                          ...prev,
                          defaults: {
                            ...(prev.defaults || CERT_DEFAULTS),
                            cert_id_suffix: e.target.value,
                          },
                        }))
                      }
                      className="w-full mt-1 bg-white border border-gray-200 rounded-xl py-2 px-3 text-sm"
                    />
                  </div>
                  <div>
                    <label className="text-[11px] font-bold text-gray-500 uppercase">Hiệu lực (tháng)</label>
                    <input
                      type="number"
                      min={1}
                      max={60}
                      value={layout.defaults?.validity_months ?? CERT_DEFAULTS.validity_months}
                      onChange={(e) =>
                        setLayout((prev) => ({
                          ...prev,
                          defaults: {
                            ...(prev.defaults || CERT_DEFAULTS),
                            validity_months: Number(e.target.value) || 6,
                          },
                        }))
                      }
                      className="w-full mt-1 bg-white border border-gray-200 rounded-xl py-2 px-3 text-sm"
                    />
                  </div>
                </div>
              </div>
            </div>

            <aside className="w-full lg:w-80 shrink-0 overflow-y-auto p-4 md:p-6 bg-gray-50/80 border-t lg:border-t-0 border-gray-100">
              <div className="sticky top-0 z-10 bg-gray-50/95 backdrop-blur-sm pb-4 mb-4">
                <div className="flex rounded-2xl bg-white border border-gray-200 p-1">
                  {[
                    { id: "fields", label: "Trường" },
                    { id: "properties", label: "Thuộc tính" },
                    { id: "preview", label: "Xem trước" },
                  ].map((tab) => (
                    <button
                      key={tab.id}
                      type="button"
                      onClick={() => setRightPanelTab(tab.id)}
                      className={`flex-1 rounded-xl px-3 py-2 text-xs font-black uppercase tracking-wide transition-all ${
                        rightPanelTab === tab.id
                          ? "bg-red-600 text-white shadow-sm"
                          : "text-gray-500 hover:text-red-600"
                      }`}
                    >
                      {tab.label}
                    </button>
                  ))}
                </div>
              </div>

              {rightPanelTab === "fields" ? (
                <div>
                  <h3 className="text-xs font-black uppercase text-gray-400 tracking-widest mb-2">
                    Trường hiển thị trên giấy
                  </h3>
                  <p className="text-[11px] text-gray-500 mb-2">
                    Đang bật <strong className="text-gray-800">{enabledCount}</strong> / {DEFAULT_FIELD_KEYS.length} trường.
                    Chỉ trường được bật mới in khi cấp chứng nhận.
                  </p>
                  <div className="flex flex-wrap gap-1.5 mb-3">
                    <button
                      type="button"
                      onClick={() => applyPresetKeys(IEC_PRESET_KEYS)}
                      className="px-2.5 py-1 rounded-lg border border-red-200 bg-red-50 text-[10px] font-bold text-red-800 hover:bg-red-100"
                    >
                      Mẫu IEC (9)
                    </button>
                    <button
                      type="button"
                      onClick={() => applyPresetKeys(DEFAULT_FIELD_KEYS)}
                      className="px-2.5 py-1 rounded-lg border border-gray-200 bg-white text-[10px] font-bold text-gray-700 hover:bg-gray-50"
                    >
                      Bật tất cả
                    </button>
                    <button
                      type="button"
                      onClick={() => applyPresetKeys([])}
                      className="px-2.5 py-1 rounded-lg border border-gray-200 bg-white text-[10px] font-bold text-gray-500 hover:bg-gray-50"
                    >
                      Tắt hết
                    </button>
                  </div>
                  <div className="rounded-xl border border-gray-200 bg-white p-2 space-y-1">
                    {DEFAULT_FIELD_KEYS.map((key) => {
                      const on = isFieldKeyEnabled(layout, key);
                      return (
                        <label
                          key={key}
                          className={`flex items-start gap-2 rounded-lg px-2 py-1.5 cursor-pointer text-xs ${
                            on ? "bg-red-50" : "hover:bg-gray-50"
                          }`}
                        >
                          <input
                            type="checkbox"
                            checked={on}
                            onChange={(e) => setFieldEnabled(key, e.target.checked)}
                            className="mt-0.5 shrink-0 accent-red-700"
                          />
                          <span>
                            <span className="font-bold text-gray-800 block">
                              {CERT_FIELD_LABELS[key] || key}
                            </span>
                            <span className="text-[10px] text-gray-400 font-mono">{key}</span>
                          </span>
                        </label>
                      );
                    })}
                  </div>
                </div>
              ) : null}

              {rightPanelTab === "properties" ? (
                <>
                  <h3 className="text-xs font-black uppercase text-gray-400 tracking-widest mb-3">
                    Thuộc tính trường
                  </h3>
                  {selectedField ? (
                    <div className="space-y-3">
                      <div className="rounded-xl border border-red-100 bg-red-50 px-3 py-2">
                        <p className="text-sm font-bold text-gray-800">
                          {CERT_FIELD_LABELS[selectedField.key] || selectedField.key}
                        </p>
                        <p className="text-[11px] text-gray-500 mt-1">
                          Đang chỉnh field <span className="font-mono text-gray-700">{selectedField.key}</span>. Dùng snap lưới + nudge để căn thật đều.
                        </p>
                      </div>
                      <div>
                        <label className="text-[11px] font-bold text-gray-500 uppercase">Cỡ chữ (px)</label>
                        <input
                          type="range"
                          min={10}
                          max={56}
                          value={selectedField.fontSize ?? 18}
                          onChange={(e) => updateSelected({ fontSize: Number(e.target.value) })}
                          className="w-full mt-1"
                        />
                        <div className="text-xs text-gray-600 font-mono">{selectedField.fontSize ?? 18}px</div>
                      </div>
                      <div>
                        <label className="text-[11px] font-bold text-gray-500 uppercase">Font chữ</label>
                        <select
                          value={selectedField.fontFamily || "times"}
                          onChange={(e) => updateSelected({ fontFamily: e.target.value })}
                          className="w-full mt-1 bg-white border border-gray-200 rounded-xl py-2 px-3 text-sm font-bold"
                        >
                          {CERT_FONT_OPTIONS.map((font) => (
                            <option key={font.id} value={font.id}>
                              {font.name}
                            </option>
                          ))}
                        </select>
                      </div>
                      <div>
                        <label className="text-[11px] font-bold text-gray-500 uppercase">Độ rộng vùng chữ (px)</label>
                        <input
                          type="range"
                          min={60}
                          max={760}
                          value={selectedField.width ?? 200}
                          onChange={(e) => updateSelected({ width: Number(e.target.value) })}
                          className="w-full mt-1"
                        />
                        <div className="text-xs text-gray-600 font-mono">{selectedField.width ?? 200}px</div>
                      </div>
                      <div>
                        <label className="text-[11px] font-bold text-gray-500 uppercase">Màu chữ</label>
                        <input
                          type="color"
                          value={selectedField.color || "#111827"}
                          onChange={(e) => updateSelected({ color: e.target.value })}
                          className="mt-1 h-10 w-full rounded-lg border border-gray-200 cursor-pointer"
                        />
                      </div>
                      <div>
                        <label className="text-[11px] font-bold text-gray-500 uppercase">Căn ngang</label>
                        <select
                          value={selectedField.align || "center"}
                          onChange={(e) => updateSelected({ align: e.target.value })}
                          className="w-full mt-1 bg-white border border-gray-200 rounded-xl py-2 px-3 text-sm font-bold"
                        >
                          <option value="left">Trái</option>
                          <option value="center">Giữa</option>
                          <option value="right">Phải</option>
                        </select>
                      </div>
                      <div>
                        <label className="text-[11px] font-bold text-gray-500 uppercase">Kiểu chữ</label>
                        <select
                          value={selectedField.fontStyle || "normal"}
                          onChange={(e) => updateSelected({ fontStyle: e.target.value })}
                          className="w-full mt-1 bg-white border border-gray-200 rounded-xl py-2 px-3 text-sm font-bold"
                        >
                          <option value="normal">Thường</option>
                          <option value="italic">In nghiêng</option>
                        </select>
                      </div>
                      <div>
                        <label className="text-[11px] font-bold text-gray-500 uppercase">Chuyển kiểu chữ</label>
                        <select
                          value={selectedField.textTransform || "none"}
                          onChange={(e) => updateSelected({ textTransform: e.target.value })}
                          className="w-full mt-1 bg-white border border-gray-200 rounded-xl py-2 px-3 text-sm font-bold"
                        >
                          {TEXT_TRANSFORM_OPTIONS.map((opt) => (
                            <option key={opt.value} value={opt.value}>
                              {opt.label}
                            </option>
                          ))}
                        </select>
                      </div>
                      <div>
                        <label className="text-[11px] font-bold text-gray-500 uppercase">Độ đậm</label>
                        <select
                          value={selectedField.fontWeight || "normal"}
                          onChange={(e) => updateSelected({ fontWeight: e.target.value })}
                          className="w-full mt-1 bg-white border border-gray-200 rounded-xl py-2 px-3 text-sm font-bold"
                        >
                          <option value="normal">Thường</option>
                          <option value="bold">Đậm</option>
                        </select>
                      </div>
                      <div className="rounded-xl border border-gray-200 bg-white p-3">
                        <label className="text-[11px] font-bold text-gray-500 uppercase block mb-2">Vị trí chính xác</label>
                        <div className="grid grid-cols-2 gap-2">
                          <div>
                            <label className="text-[10px] font-semibold text-gray-400 uppercase">X (%)</label>
                            <input
                              type="number"
                              min={POSITION_MIN * 100}
                              max={POSITION_MAX * 100}
                              step={Math.max(0.01, currentNudgeStep * 100)}
                              value={((selectedField.x ?? 0.5) * 100).toFixed(2)}
                              onChange={(e) => updateSelectedAxis("x", e.target.value)}
                              className="w-full mt-1 bg-gray-50 border border-gray-200 rounded-xl py-2 px-3 text-sm font-semibold"
                            />
                          </div>
                          <div>
                            <label className="text-[10px] font-semibold text-gray-400 uppercase">Y (%)</label>
                            <input
                              type="number"
                              min={POSITION_MIN * 100}
                              max={POSITION_MAX * 100}
                              step={Math.max(0.01, currentNudgeStep * 100)}
                              value={((selectedField.y ?? 0.5) * 100).toFixed(2)}
                              onChange={(e) => updateSelectedAxis("y", e.target.value)}
                              className="w-full mt-1 bg-gray-50 border border-gray-200 rounded-xl py-2 px-3 text-sm font-semibold"
                            />
                          </div>
                        </div>
                        <div className="mt-3">
                          <p className="text-[10px] font-semibold text-gray-400 uppercase mb-2">
                            Dịch chuyển từng nhịp ({(currentNudgeStep * 100).toFixed(2)}%)
                          </p>
                          <div className="grid grid-cols-3 gap-2 max-w-[180px]">
                            <div />
                            <button
                              type="button"
                              onClick={() => nudgeSelected(0, -currentNudgeStep)}
                              className="h-9 rounded-lg border border-gray-200 bg-gray-50 hover:bg-gray-100 flex items-center justify-center"
                            >
                              <ArrowUp size={16} />
                            </button>
                            <div />
                            <button
                              type="button"
                              onClick={() => nudgeSelected(-currentNudgeStep, 0)}
                              className="h-9 rounded-lg border border-gray-200 bg-gray-50 hover:bg-gray-100 flex items-center justify-center"
                            >
                              <ArrowLeft size={16} />
                            </button>
                            <button
                              type="button"
                              onClick={() => {
                                updateFieldPosition(selectedField.key, 0.5, 0.5);
                              }}
                              className="h-9 rounded-lg border border-red-200 bg-red-50 text-red-700 hover:bg-red-100 text-[11px] font-bold"
                            >
                              Giữa
                            </button>
                            <button
                              type="button"
                              onClick={() => nudgeSelected(currentNudgeStep, 0)}
                              className="h-9 rounded-lg border border-gray-200 bg-gray-50 hover:bg-gray-100 flex items-center justify-center"
                            >
                              <ArrowRight size={16} />
                            </button>
                            <div />
                            <button
                              type="button"
                              onClick={() => nudgeSelected(0, currentNudgeStep)}
                              className="h-9 rounded-lg border border-gray-200 bg-gray-50 hover:bg-gray-100 flex items-center justify-center"
                            >
                              <ArrowDown size={16} />
                            </button>
                            <div />
                          </div>
                        </div>
                      </div>
                    </div>
                  ) : (
                    <div className="rounded-xl border border-dashed border-gray-300 bg-white p-4 text-sm text-gray-500">
                      Chọn một trường trên khung thiết kế để chỉnh sửa thuộc tính.
                    </div>
                  )}
                </>
              ) : null}

              {rightPanelTab === "preview" ? (
                <div>
                  <h3 className="text-xs font-black uppercase text-gray-400 tracking-widest mb-2">
                    Xem trước mẫu
                  </h3>
                  <p className="text-[11px] text-gray-500 mb-3">
                    Bản xem nhanh giúp kiểm tra tổng thể bố cục mà không phải cuộn toàn bộ panel chỉnh sửa.
                  </p>
                  <div className="rounded-xl border border-gray-200 bg-gray-100 p-2">
                    <div
                      className="relative overflow-hidden rounded-md border border-gray-200 bg-white mx-auto"
                      style={{ width: 250, height: 177 }}
                    >
                      {effectiveBackgroundUrl ? (
                        <img
                          src={effectiveBackgroundUrl}
                          alt=""
                          className="absolute inset-0 h-full w-full object-cover"
                          draggable={false}
                        />
                      ) : null}
                      {(layout.fields || []).map((f) => (
                        <div
                          key={`mini-${f.key}`}
                          className="absolute whitespace-pre-wrap break-words"
                          style={{
                            left: `${(f.x ?? 0.5) * 100}%`,
                            top: `${(f.y ?? 0.5) * 100}%`,
                            transform: miniAnchor(f.align || "center"),
                            color: f.color || "#111827",
                            fontSize: `${Math.max(7, Math.round((f.fontSize ?? 18) * 0.25))}px`,
                            fontWeight: f.fontWeight || "normal",
                            fontFamily: getCertificateFontFamily(f.fontFamily),
                            fontStyle: f.fontStyle || "normal",
                            textTransform: f.textTransform || "none",
                            textAlign: textAlignFor(f.align || "center"),
                            lineHeight: 1.2,
                            width: f.width ? `${Math.max(20, Math.round(f.width * (250 / CERT_CANVAS.width)))}px` : undefined,
                            maxWidth: "90%",
                          }}
                        >
                          {getCertFieldDisplayValue(f.key, SAMPLE_DATA, layout)}
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              ) : null}
            </aside>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

export default TemplateDesignerModal;
